import { c as _c } from "react/compiler-runtime";
import { useCallback } from 'react';
import { useAppRouter } from '@hooks/navigation';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { AppRouteEnum, CallDirectionEnum, ChannelEnum, ContractTypeEnum, NavigationSourceQueryEnum, PersistedStateKeyEnum } from '@lib/enums';
import { resolveWrapUpDestination } from '@lib/utils/resolveWrapUpDestination';
import { quoteWorkflowSchema } from '@quotes/_workflow/schema';
import { useJotaiValue } from '@states/jotai/hooks';
import { contactSelector } from '@states/softphone/contactSelector';
import { currentCallAtom } from '@states/softphone/currentCallAtom';
import type { TSalesWorkflow } from '@hooks/workflows';
import type { TCommonContract, TQuote } from '@models';
export type TUseContactRoutingReturn = {
  goToWaitingPageForNextContact: () => void;
  goToSearchPageForCurrentContact: (options?: GoToSearchPageForCurrentContactOptions) => void;
  goToWrapUpCall: (quoteId?: Maybe<string>, consumerId?: Maybe<string>, replace?: boolean) => void;
  goToHomeWrapUpCall: (quoteId: string) => void;
  goToQuoteFlowEntry: (quoteId: string) => void;
  goToHomeQuoteFlowEntry: (homeQuoteId: number) => void;
  goToInProgressQuote: (workflow: TSalesWorkflow, quote: TQuote, replace?: boolean) => void;
  goToContactConfirmation: () => void;
  goToEcommerceCallback: () => void;
  goToConsumerProfile: (consumerId: string | number) => void;
  goToConsumerVehicle: (consumerId: number, consumerVehicleId: number) => void;
  goToConsumerHome: (consumerId: number, consumerHomeId: number) => void;
  goToConsumerAsset: (contract: TCommonContract) => void;
  goToMoxyCustomerNumberSearch: () => void;
  goToDashboard: () => void;
  goToContactPreview: () => void;
  goToExternalTransfer: () => void;
  goToContractDetails: (contractId: number) => void;
  goToContractDetailsByNumber: (contractNumber: string, replace?: boolean) => void;
};
type GoToSearchPageForCurrentContactOptions = {
  clearPreviousSearchResults?: boolean;
  replace?: boolean;
};
export function useContactRouter() {
  const $ = _c(70);
  const router = useAppRouter();
  const {
    isCustomerService,
    isRetention
  } = useCurrentUser();
  const {
    contactId,
    channel,
    callDirection,
    transferType
  } = useJotaiValue(contactSelector);
  const {
    lastTouchedQuoteId,
    lastTouchedConsumerId,
    lastTouchedHomeQuoteId,
    lastTouchedQuoteType
  } = useJotaiValue(currentCallAtom);
  let t0;
  if ($[0] !== router) {
    t0 = consumerId => {
      router.build(AppRouteEnum.ConsumerProfile).setId(consumerId).go();
    };
    $[0] = router;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const goToConsumerProfile = t0;
  let t1;
  if ($[2] !== router) {
    t1 = (consumerId_0, consumerVehicleId) => {
      router.build(AppRouteEnum.ConsumerVehicle).setId(consumerId_0).setId(consumerVehicleId).go();
    };
    $[2] = router;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const goToConsumerVehicle = t1;
  let t2;
  if ($[4] !== router) {
    t2 = (consumerId_1, consumerHomeId) => {
      router.build(AppRouteEnum.ConsumerHome).setId(consumerId_1).setId(consumerHomeId).go();
    };
    $[4] = router;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const goToConsumerHome = t2;
  let t3;
  if ($[6] !== goToConsumerHome || $[7] !== goToConsumerVehicle) {
    t3 = contract => {
      if (contract.contractType === ContractTypeEnum.Home) {
        goToConsumerHome(contract.consumerId, contract.consumerHomeId);
        return;
      }
      goToConsumerVehicle(contract.consumerId, contract.consumerVehicleId);
    };
    $[6] = goToConsumerHome;
    $[7] = goToConsumerVehicle;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const goToConsumerAsset = t3;
  let t4;
  if ($[9] !== router) {
    t4 = () => {
      router.build(AppRouteEnum.ContactConfirmation).go();
    };
    $[9] = router;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const goToContactConfirmation = t4;
  let t5;
  if ($[11] !== router) {
    t5 = () => {
      router.build(AppRouteEnum.ContactEcommerceCallback).go();
    };
    $[11] = router;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const goToEcommerceCallback = t5;
  let t6;
  if ($[13] !== router) {
    t6 = () => {
      router.build(AppRouteEnum.Dashboard).go();
    };
    $[13] = router;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const goToDashboard = t6;
  let t7;
  if ($[15] !== router) {
    t7 = () => {
      router.build(AppRouteEnum.ContactSearchMoxyCustomerNumber).go();
    };
    $[15] = router;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const goToMoxyCustomerNumberSearch = t7;
  let t8;
  if ($[17] !== router) {
    t8 = quoteId => {
      router.build(AppRouteEnum.QuoteFlowEntry).setId(quoteId).go();
    };
    $[17] = router;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const goToQuoteFlowEntry = t8;
  let t9;
  if ($[19] !== router) {
    t9 = homeQuoteId => {
      router.build(AppRouteEnum.HomeQuoteFlowEntry).setId(homeQuoteId).go();
    };
    $[19] = router;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const goToHomeQuoteFlowEntry = t9;
  let t10;
  if ($[21] !== router) {
    t10 = quoteId_0 => {
      router.build(AppRouteEnum.HomeQuoteFlowWrapUp).setId(quoteId_0).go();
    };
    $[21] = router;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const goToHomeWrapUpCall = t10;
  let t11;
  if ($[23] !== router) {
    t11 = () => {
      router.build(AppRouteEnum.ContactWaiting).go();
    };
    $[23] = router;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const goToWaitingPageForNextContact = t11;
  let t12;
  if ($[25] !== router) {
    t12 = () => {
      router.build(AppRouteEnum.ContactPreview).go();
    };
    $[25] = router;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const goToContactPreview = t12;
  let t13;
  if ($[27] !== router) {
    t13 = () => {
      router.build(AppRouteEnum.ContactExternalTransfer).go();
    };
    $[27] = router;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const goToExternalTransfer = t13;
  let t14;
  if ($[29] !== callDirection || $[30] !== channel || $[31] !== goToMoxyCustomerNumberSearch || $[32] !== isCustomerService || $[33] !== isRetention || $[34] !== router || $[35] !== transferType) {
    t14 = options => {
      const clearPreviousSearchResults = options?.clearPreviousSearchResults ?? true;
      const routingConfig = options?.replace != null ? {
        replace: options.replace
      } : undefined;
      if (clearPreviousSearchResults) {
        localStorage.removeItem(PersistedStateKeyEnum.DirectMailSearchType);
        localStorage.removeItem(PersistedStateKeyEnum.DirectMailSearchFormState);
        localStorage.removeItem(PersistedStateKeyEnum.ManualSearchFormState);
        localStorage.removeItem(PersistedStateKeyEnum.SearchType);
        localStorage.removeItem(PersistedStateKeyEnum.SearchMoxyCustomerNumberFormState);
      }
      if (isCustomerService) {
        router.build(AppRouteEnum.Dashboard).go();
        return;
      }
      if (isRetention) {
        router.build(AppRouteEnum.ContactSearchAuto).go(routingConfig);
        return;
      }
      if (transferType != null) {
        goToMoxyCustomerNumberSearch();
        return;
      }
      if (channel === ChannelEnum.DirectMail && callDirection === CallDirectionEnum.Inbound) {
        router.build(AppRouteEnum.ContactSearchMail).go(routingConfig);
        return;
      }
      router.build(AppRouteEnum.ContactSearchAuto).go(routingConfig);
    };
    $[29] = callDirection;
    $[30] = channel;
    $[31] = goToMoxyCustomerNumberSearch;
    $[32] = isCustomerService;
    $[33] = isRetention;
    $[34] = router;
    $[35] = transferType;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const goToSearchPageForCurrentContact = t14;
  let t15;
  if ($[37] !== contactId || $[38] !== lastTouchedConsumerId || $[39] !== lastTouchedHomeQuoteId || $[40] !== lastTouchedQuoteId || $[41] !== lastTouchedQuoteType || $[42] !== router) {
    t15 = (quoteId_1, consumerId_2, t16) => {
      const replace = t16 === undefined ? false : t16;
      const destination = resolveWrapUpDestination({
        consumerId: consumerId_2,
        contactId,
        lastTouchedConsumerId,
        lastTouchedHomeQuoteId,
        lastTouchedQuoteId,
        lastTouchedQuoteType,
        quoteId: quoteId_1
      });
      switch (destination.type) {
        case "quote":
          {
            router.build(AppRouteEnum.QuoteFlowEntry).setId(destination.quoteId).setQuery({
              source: NavigationSourceQueryEnum.WrapCall
            }).go({
              replace
            });
            return;
          }
        case "homeQuote":
          {
            router.build(AppRouteEnum.HomeQuoteFlowWrapUp).setId(destination.homeQuoteId).go({
              replace
            });
            return;
          }
        case "consumer":
          {
            router.build(AppRouteEnum.ConsumerWrapUp).setId(destination.consumerId).go({
              replace
            });
            return;
          }
        case "contact":
          {
            router.build(AppRouteEnum.ContactWrapUp).setId(destination.contactId).go({
              replace
            });
            return;
          }
        default:
          {
            return;
          }
      }
    };
    $[37] = contactId;
    $[38] = lastTouchedConsumerId;
    $[39] = lastTouchedHomeQuoteId;
    $[40] = lastTouchedQuoteId;
    $[41] = lastTouchedQuoteType;
    $[42] = router;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  const goToWrapUpCall = t15;
  let t16;
  if ($[44] !== router) {
    t16 = (workflow, quote, t17) => {
      const replace_0 = t17 === undefined ? false : t17;
      const firstStep = workflow.steps.at(0);
      const firstStepDefinition = quoteWorkflowSchema[firstStep.step];
      router.build(firstStepDefinition.slug).setId(quote.id).go({
        replace: replace_0
      });
    };
    $[44] = router;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  const goToInProgressQuote = t16;
  let t17;
  if ($[46] !== router) {
    t17 = (contractNumber, t18) => {
      const replace_1 = t18 === undefined ? false : t18;
      router.build(AppRouteEnum.ContractByNumber).setId(contractNumber).go({
        replace: replace_1
      });
    };
    $[46] = router;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  const goToContractDetailsByNumber = t17;
  let t18;
  if ($[48] !== router) {
    t18 = contractId => {
      const builder = router.build(AppRouteEnum.ContractById).setId(contractId);
      builder.go();
    };
    $[48] = router;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  const goToContractDetails = t18;
  let t19;
  if ($[50] !== goToConsumerAsset || $[51] !== goToConsumerHome || $[52] !== goToConsumerProfile || $[53] !== goToConsumerVehicle || $[54] !== goToContactConfirmation || $[55] !== goToContactPreview || $[56] !== goToContractDetails || $[57] !== goToContractDetailsByNumber || $[58] !== goToDashboard || $[59] !== goToEcommerceCallback || $[60] !== goToExternalTransfer || $[61] !== goToHomeQuoteFlowEntry || $[62] !== goToHomeWrapUpCall || $[63] !== goToInProgressQuote || $[64] !== goToMoxyCustomerNumberSearch || $[65] !== goToQuoteFlowEntry || $[66] !== goToSearchPageForCurrentContact || $[67] !== goToWaitingPageForNextContact || $[68] !== goToWrapUpCall) {
    t19 = {
      goToWaitingPageForNextContact,
      goToSearchPageForCurrentContact,
      goToWrapUpCall,
      goToHomeWrapUpCall,
      goToQuoteFlowEntry,
      goToHomeQuoteFlowEntry,
      goToInProgressQuote,
      goToContactConfirmation,
      goToEcommerceCallback,
      goToConsumerProfile,
      goToConsumerVehicle,
      goToConsumerHome,
      goToConsumerAsset,
      goToMoxyCustomerNumberSearch,
      goToDashboard,
      goToContactPreview,
      goToExternalTransfer,
      goToContractDetailsByNumber,
      goToContractDetails
    };
    $[50] = goToConsumerAsset;
    $[51] = goToConsumerHome;
    $[52] = goToConsumerProfile;
    $[53] = goToConsumerVehicle;
    $[54] = goToContactConfirmation;
    $[55] = goToContactPreview;
    $[56] = goToContractDetails;
    $[57] = goToContractDetailsByNumber;
    $[58] = goToDashboard;
    $[59] = goToEcommerceCallback;
    $[60] = goToExternalTransfer;
    $[61] = goToHomeQuoteFlowEntry;
    $[62] = goToHomeWrapUpCall;
    $[63] = goToInProgressQuote;
    $[64] = goToMoxyCustomerNumberSearch;
    $[65] = goToQuoteFlowEntry;
    $[66] = goToSearchPageForCurrentContact;
    $[67] = goToWaitingPageForNextContact;
    $[68] = goToWrapUpCall;
    $[69] = t19;
  } else {
    t19 = $[69];
  }
  return t19;
}